export type WSMessage = {
  event: string;
  payload: unknown;
};

export class WSManager<T extends object, R extends object> {
  private readonly url: string;
  private _sessionId?: string;
  private socket: WebSocket | null = null;
  private isReady = false;
  private listeners = new Set<(msg: T) => void>();
  private messageQueue: R[] = [];
  private abortController = new AbortController();
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private shouldReconnect = false;

  constructor(url: string) {
    this.url = url;
  }

  public connect() {
    this.shouldReconnect = true;
    this.initSocket();
  }

  initSocket() {
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    this.socket = new WebSocket(
      `${this.url}${this._sessionId ? `?id=${this._sessionId}` : ''}`,
    );
    this.setupListeners();
  }

  setupListeners() {
    if (!this.socket) return;

    const options = { signal: this.abortController.signal };

    this.socket.addEventListener('open', this.handleOpen, options);
    this.socket.addEventListener('message', this.handleMessage, options);
    this.socket.addEventListener('close', this.handleClose, options);
    this.socket.addEventListener('error', this.handleError, options);
  }

  private handleOpen = () => {
    this.isReady = true;
    this.startHeartbeat();
    this.flushMessageQueue();
  };

  private handleMessage = (ev: MessageEvent) => {
    if (ev.data === 'pong') {
      return;
    }

    let msg;

    try {
      msg = JSON.parse(ev.data);
    } catch (error) {
      console.error('Ошибка парсинга сообщения:', error);
      return;
    }

    for (const listener of this.listeners) {
      listener(msg);
    }
  };

  private handleClose = () => {
    console.log('WebSocket close');
    this.isReady = false;
    this.stopHeartbeat();

    if (this.socket) {
      this.abortController.abort();
      this.socket = null;
    }

    if (this.shouldReconnect) {
      this.initSocket();
    }
  };

  private handleError = (err: Event) => {
    this.isReady = false;
    console.error('WebSocket error:', err);
  };

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send('ping');
      }
    }, 25_000);
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private flushMessageQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      if (message) {
        this.sendMessage(message);
      }
    }
  }

  sendMessage(data: R) {
    if (!this.socket) {
      this.connect();
    }

    if (this.socket && this.isReady) {
      const message = JSON.stringify(data);
      this.socket.send(message);
    } else {
      console.warn('Cannot send message, socket not ready');
      this.messageQueue.push(data);
    }
  }

  addListener(callback: (msg: T) => void) {
    if (!this.listeners.has(callback)) {
      this.listeners.add(callback);
    }
  }

  removeListener(callback: (msg: T) => void) {
    if (this.listeners.has(callback)) {
      this.listeners.delete(callback);
    }
  }

  isSocketReady() {
    return this.isReady;
  }

  close() {
    this.shouldReconnect = false;
    this.stopHeartbeat();
    if (this.socket) {
      this.abortController.abort();
      this.socket.close(1000);
      this.socket = null;
      this.isReady = false;
    }
  }

  public get sessionId() {
    return this._sessionId;
  }

  public setSessionId(sessionId: string) {
    this._sessionId = sessionId;
  }
}
